import type { MutableRefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { getRecentSearches, saveRecentSearches } from '../../../utils/recent-searches';
import { getSelectHandler } from './suggestions/handlers';

interface UseRecentSearchOptions {
  isEnabled: boolean;
  isSuggestionsOpen: boolean;
  isInputFocused: boolean;
  isContainerHidden: boolean;
  isInputLocked: boolean;
  isMobile: boolean;
  inputValue: string;
  inputRef: MutableRefObject<HTMLInputElement>;
}

const useGetInitialSearches = (isEnabled: boolean) =>
  useMemo(() => (isEnabled ? getRecentSearches() : []), [isEnabled]);

const useHandlers = (isEnabled: boolean) => {
  const initialSearches = useGetInitialSearches(isEnabled);
  const [recentSearches, setRecentSearches] = useState(initialSearches);

  useEffect(() => {
    if (initialSearches !== recentSearches) {
      saveRecentSearches(recentSearches);
    }
  }, [recentSearches, initialSearches]);

  const removeAll = () => setRecentSearches([]);
  const remove = (index: number) =>
    setRecentSearches([...recentSearches.slice(0, index), ...recentSearches.slice(index + 1)]);

  const listItems = recentSearches.slice(0, 10).map(({ keyword }) => keyword);
  const handleSelect = getSelectHandler({ listItems, isRecentSearch: true });

  return {
    recentSearches,
    listItems,
    handlers: { removeAll, remove, handleSelect },
  };
};

const shouldOpen = ({
  isSuggestionsOpen,
  isInputLocked,
  isInputFocused,
  isContainerHidden,
  inputValue,
  isMobile,
}: Omit<UseRecentSearchOptions, 'isEnabled' | 'inputRef'>) => {
  const isOpeningMobile = isMobile && isInputLocked;
  const isOpeningDesktop = !isMobile && !isContainerHidden;

  return (
    !isSuggestionsOpen &&
    isInputFocused &&
    inputValue.length === 0 &&
    (isOpeningMobile || isOpeningDesktop)
  );
};

const shouldClose = ({
  isSuggestionsOpen,
  isMobile,
  isInputLocked,
  isInputFocused,
  isContainerHidden,
  inputValue,
}: Omit<UseRecentSearchOptions, 'isEnabled' | 'inputRef'>) => {
  const isClosingMobile = isMobile && !isInputLocked && !isInputFocused;
  const isClosingDesktop = !isMobile && isContainerHidden;

  return isSuggestionsOpen || inputValue.length > 0 || isClosingDesktop || isClosingMobile;
};

export const useRecentSearches = ({ inputRef, isEnabled, ...options }: UseRecentSearchOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const { recentSearches, listItems, handlers } = useHandlers(isEnabled);

  useEffect(() => {
    if (recentSearches.length === 0) {
      return;
    }
    if (!isOpen && shouldOpen(options)) {
      setIsOpen(true);
    }
    if (isOpen && shouldClose(options)) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    if (recentSearches.length === 0 && isOpen) {
      setIsOpen(false);
      inputRef.current.focus();
    }
  }, [recentSearches]);

  if (!isEnabled) {
    return null;
  }

  return {
    list: recentSearches,
    listItems,
    flags: { isOpen },
    handlers,
  };
};
